import { createStyles, makeStyles, Theme } from "@material-ui/core";
import Slider from "@material-ui/core/Slider";
import Typography from "@material-ui/core/Typography";
import { max } from "mathjs";
import { Chart } from "react-google-charts";
import {
	DATA_TYPES,
	FONTS,
	PAIR_PLOT_RADIALS,
	PRIMARY_COLOR,
	SECONDARY_COLOR,
	THIRD_COLOR,
} from "../../constants";
import {
	addAlpha,
	calcMax,
	calcMin,
	convertToNumber,
	isNaN2,
} from "../../helpers/DataHelpers";
import { formatNumber } from "../../helpers/FormatNumber";
import Column from "../../MachineLearning/Column";
import Dataset from "../../MachineLearning/Dataset";
import { stateType, stateTypeF } from "../../state";
import { HelpIcon } from "../Common/ToolTipIcon";
import { RadioButtonsGroup } from "./DualChartBox";
import { Scatter } from "./Scatter";
import { Scatter2 } from "./Scatter2";
const math = require("mathjs");

export const BAR_COLORS = [PRIMARY_COLOR, THIRD_COLOR];
export const MAX_SCATTER_POINTS = 2000;

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		chartContainer: {
			width: "90%",
			height: "60%",
			marginLeft: "5%",
		},
		chartContainer2: {
			width: "100%",
			height: "100%",
			display: "flex",
			flexDirection: "column",
			position: "relative",
		},
		legendContainer: {
			display: "flex",
			flexDirection: "row",
			marginTop: "-280px",
			marginLeft: "20px",
			width: "100%",
			justifyContent: "center",
			marginBottom: "-10px",
			zIndex: 100,
		},
		legendItemContainer: {
			display: "flex",
			flexDirection: "row",
			zIndex: 100,
		},
		ellipse: {
			fontSize: "15px",
			fontFamily: FONTS,
			marginLeft: "15px",
			marginRight: "-15px",
			lineHeight: "22px",
			fontWeight: "bold",
			color: "rgba(0,0,0,0.6)",
			width: "50px",
			zIndex: 1000,
		},
		sliderContainer: {
			width: "100%",
			justifyContent: "center",
			alignItems: "center",
		},
		titleCorContainer: {
			width: "100%",
			display: "flex",
			flexDirection: "column",
			justifyContent: "spaceBetween",
			marginBottom: "10px",
			marginTop: "5px",
		},
	})
);

export default function DiscreteSlider({ state }: stateType) {
	const classes = useStyles();

	function handleChange(id, val) {
		if (val === state.jiggleFactor) {
			return;
		}
		state.setJiggleFactor(val);
	}

	return (
		<div className={classes.sliderContainer}>
			<Typography
				style={{ fontFamily: FONTS, marginBottom: "-5px" }}
				id="discrete-slider-small-steps"
				gutterBottom
			>
				Jiggle Factor
			</Typography>
			<Slider
				defaultValue={5}
				aria-labelledby="discrete-slider-small-steps"
				step={1}
				marks
				min={0}
				max={10}
				valueLabelDisplay="auto"
				onChange={handleChange}
				style={{ width: "300px" }}
			/>
		</div>
	);
}

export function ChartXTitle({ xName, isBar }) {
	let marg = "305px";
	if (isBar) {
		marg = "305px";
	}

	return (
		<div
			style={{
				color: "rgba(0,0,0,0.6)",
				fontFamily: FONTS,
				fontSize: "14px",
				zIndex: 1000,
				width: "100%",
				marginBottom: "-" + marg,
				marginLeft: "20px",
				marginTop: marg,
				height: "0px",
				textAlign: "center",
			}}
		>
			{xName}
		</div>
	);
}

export function ChartYTitle({ yName }) {
	return (
		<div
			style={{
				color: "rgba(0,0,0,0.6)",
				fontFamily: FONTS,
				fontSize: "14px",
				zIndex: 100,
				width: "100%",
				height: "0%",
				position: "relative",
				textAlign: "center",
				transform: "rotate(-90deg)",
				left: "-55%",
				bottom: "-40%",
			}}
		>
			{yName}
		</div>
	);
}

export function getUsableColumns(dataset: Dataset) {
	let xCol: Column = undefined;
	let yCol: Column = undefined;

	let i = 0;
	while (i < dataset.columns.length) {
		if (dataset.columns[i].columnType !== DATA_TYPES.unusable) {
			xCol = dataset.columns[i];
			i++;
			break;
		}
		i++;
	}
	while (i < dataset.columns.length) {
		if (dataset.columns[i].columnType !== DATA_TYPES.unusable) {
			yCol = dataset.columns[i];
			i++;
			break;
		}
		i++;
	}
	return { xCol, yCol };
}

function getCol(state: stateTypeF) {
	let ds = state.saveState.correlations.dualChartSelected;
	let xCol = state.saveState.dataset.getColumn(ds[0]);
	let yCol = state.saveState.dataset.getColumn(ds[1]);

	if (xCol === undefined || yCol === undefined) {
		let outCols = getUsableColumns(state.saveState.dataset);
		xCol = outCols.xCol;
		yCol = outCols.yCol;
	}

	if (
		yCol.columnType == DATA_TYPES.categorical &&
		xCol.columnType == DATA_TYPES.number
	) {
		let tmp = xCol;
		xCol = yCol;
		yCol = tmp;
	}

	return { xCol, yCol };
}

type getChartType = { xCol: Column; yCol: Column; state: stateTypeF };
function GetChart({ state, xCol, yCol }: getChartType) {
	if (
		xCol.columnType === DATA_TYPES.categorical &&
		yCol.columnType === DATA_TYPES.categorical
	) {
		return <BarChart state={state} xCol={xCol} yCol={yCol}></BarChart>;
	} else {
		return (
			<ScatterChart state={state} xCol={xCol} yCol={yCol}></ScatterChart>
		);
	}
}

type legendType = {
	yCol: Column;
	legendNames?: string[];
	visible?: boolean;
	colors?: string[];
	isSame?: boolean;
};
type legendItemType = { color: string; name: string };

function LegendItem({ color, name }: legendItemType) {
	const classes = useStyles();
	return (
		<div className={classes.legendItemContainer}>
			<div
				style={{
					width: "20px",
					height: "20px",
					backgroundColor: color,
					marginLeft: "10px",
				}}
			></div>
			<div
				style={{
					lineHeight: "20px",
					fontSize: "12px",
					marginLeft: "5px",
					fontFamily: FONTS,
					color: "rgba(0,0,0,0.8)",
				}}
			>
				{name}
			</div>
		</div>
	);
}

function getLegendColors(colors: string[]) {
	const mid = Math.ceil(colors.length / 2);
	for (let i = 0; i < mid; i++) {
		colors[i] = addAlpha(BAR_COLORS[0], 1 - i / (mid + 2));
	}
	for (let i = mid; i < colors.length; i++) {
		colors[i] = addAlpha(BAR_COLORS[1], 1 - (i - mid) / colors.length);
	}
	return colors;
}

export function getBarColors(colors: string[]) {
	const mid = Math.ceil(colors.length / 2);
	let alphas: number[] = Array(colors.length).fill(0);
	for (let i = 0; i < mid; i++) {
		alphas[i] = 1 - i / (mid + 1);
		colors[i] = BAR_COLORS[0];
	}
	for (let i = mid; i < colors.length; i++) {
		alphas[i] = 1 - (i - mid) / colors.length;
		colors[i] = BAR_COLORS[1];
	}
	return { colors: colors, alphas: alphas };
}

export function getBarColorsSame(colors: string[]) {
	let alphas: number[] = Array(colors.length).fill(0);

	for (let i = 0; i < colors.length; i++) {
		alphas[i] = 1;
		colors[i] = BAR_COLORS[i % 2];
	}
	return { colors: colors, alphas: alphas };
}

export function Legend({
	yCol,
	legendNames = undefined,
	visible = true,
	colors = undefined,
	isSame = false,
}: legendType) {
	const classes = useStyles();
	if (yCol === undefined) {
		return <div></div>;
	}

	const maxLegend = 6;
	if (legendNames === undefined) {
		let tmp = new Set(
			Array(yCol.allClasses.length)
				.fill(0)
				.map((v, i) => yCol.getClassByIndex(i, true))
		);
		legendNames = yCol.nonSparseClasses;
		if (tmp.has("Other Classes")) {
			legendNames = [...yCol.nonSparseClasses, "Other Classes"];
		}
	}
	const legendOverflow = legendNames.length > maxLegend;
	if (colors === undefined) {
		let outColors = getBarColors(Array(legendNames.length).fill(""));
		if (isSame) {
			outColors = getBarColorsSame(Array(legendNames.length).fill(""));
		}
		colors = outColors.colors.map((val, i) =>
			addAlpha(val, outColors.alphas[i])
		);
	}

	if (legendOverflow) {
		legendNames = [
			...legendNames.slice(0, 3),
			...legendNames.slice(legendNames.length - 3, legendNames.length),
		];
		colors = [
			...colors.slice(0, 3),
			...colors.slice(colors.length - 3, colors.length),
		];
	}

	return (
		<div className={classes.legendContainer}>
			{colors.map((c, i) => (
				<div style={{ display: "flex", flexDirection: "row" }}>
					{legendOverflow && i == 3 ? (
						<div className={classes.ellipse}>. . .</div>
					) : (
						<div></div>
					)}
					<LegendItem
						color={colors[i]}
						name={legendNames[i]}
					></LegendItem>
				</div>
			))}
		</div>
	);
}

export function subSample(leadCol: number[], followCol: number[]) {
	if (leadCol.length < MAX_SCATTER_POINTS) {
		return { leadCol, followCol };
	}

	function compare(a, b) {
		if (a[0] < b[0]) {
			return -1;
		}
		if (a[0] > b[0]) {
			return 1;
		}
		// a must be equal to b
		return 0;
	}

	let withIndex: number[][] = leadCol.map((v, i) => [v, i]);
	withIndex.sort(compare);
	let sortedLead: number[] = [];
	let sortedFollow: number[] = [];
	const stepSize = Math.floor(leadCol.length / 1000);
	let addedLast = false;
	for (let i = 0; i < leadCol.length; i += stepSize) {
		if (i === leadCol.length - 1) {
			addedLast = true;
		}
		sortedLead.push(withIndex[i][0]);
		sortedFollow.push(followCol[withIndex[i][1]]);
	}
	if (!addedLast) {
		sortedLead.push(withIndex[withIndex.length - 1][0]);
		sortedFollow.push(followCol[withIndex[withIndex.length - 1][1]]);
	}
	return { leadCol: sortedLead, followCol: sortedFollow };
}

function ScatterChart({ state, xCol, yCol }: chartInputType) {
	const classes = useStyles();

	let ticks = [];
	let minX = 0;
	let maxX = 1;
	let xData = xCol.vectorizedData;
	let yData = yCol.vectorizedData;

	// Subsample data
	if (xCol.columnType === DATA_TYPES.number) {
		let { leadCol, followCol } = subSample(xData, yData);
		xData = leadCol;
		yData = followCol;
	} else {
		let { leadCol, followCol } = subSample(yData, xData);
		yData = leadCol;
		xData = followCol;
	}

	if (xCol.columnType === DATA_TYPES.number) {
		ticks = null;
		let val = xData.filter((val) => !isNaN2(val));
		minX = calcMin(val, DATA_TYPES.number) * 0.99;
		maxX = calcMax(val, DATA_TYPES.number) * 1.01;
	} else {
		for (let i = 0; i < xCol.nonSparseClasses.length; i++) {
			ticks.push({ v: i, f: xCol.getClassByIndex(i, true) });
		}
		minX = -0.5;
		maxX = xCol.nonSparseClasses.length - 1 + 0.5;
	}

	let val = yData.filter((val) => !isNaN2(val));
	let minY = calcMin(val, DATA_TYPES.number) * 0.99;
	let maxY = calcMax(val, DATA_TYPES.number) * 1.01;

	let data: any[][] = [
		[
			{ type: "number" },
			{ type: "number" },
			{ role: "style" },
			{ role: "tooltip" },
		],
	];

	const seedrandom = require("seedrandom");
	const generator = seedrandom(100);
	var sett = `fill-color: ${THIRD_COLOR}; fill-opacity: 0.6; stroke-color: #76A7FA; stroke-width: 0;`;

	for (let i = 0; i < xData.length; i++) {
		let rnd = 0;
		if (xCol.columnType === DATA_TYPES.categorical) {
			rnd = ((generator() - 1 / 2) * state.jiggleFactor) / 15;
		}
		data.push([
			xData[i] + rnd,
			yData[i],
			sett,
			xCol.columnName +
				":" +
				xCol.getClassByIndex(xData[i], true) +
				"\n" +
				yCol.columnName +
				":" +
				yData[i],
		]);
	}
	let scatterType = xCol.availableColumnTypes.includes(DATA_TYPES.number);
	return (
		<div
			style={{
				marginTop: scatterType ? "-10px" : "40px",
				height: scatterType ? "380px" : "450px",
			}}
		>
			{scatterType ? (
				<Scatter
					data={data}
					ticks={ticks}
					minX={minX}
					maxX={maxX}
					minY={minY}
					maxY={maxY}
					xTitle={xCol.columnName}
					yTitle={yCol.columnName}
					height={scatterType ? "380px" : "450px"}
				></Scatter>
			) : (
				<Scatter2
					data={data}
					ticks={ticks}
					minX={minX}
					maxX={maxX}
					minY={minY}
					maxY={maxY}
					xTitle={xCol.columnName}
					yTitle={yCol.columnName}
					height={scatterType ? "380px" : "450px"}
					trendlines={false}
				></Scatter2>
			)}
		</div>
	);
}

type chartInputType = { state: stateTypeF; xCol: Column; yCol: Column };

function BarChart({ state, xCol, yCol }: chartInputType) {
	const classes = useStyles();
	const isSame = xCol.columnName === yCol.columnName;
	let data: any[][];

	const nClassesX = calcMax(xCol.vectorizedData, DATA_TYPES.number) + 1;
	const nClassesY = isSame
		? 1
		: calcMax(yCol.vectorizedData, DATA_TYPES.number) + 1;

	let counts = Array(nClassesX)
		.fill(0)
		.map((v) => Array(nClassesY).fill(0));

	for (let i = 0; i < xCol.vectorizedData.length; i++) {
		let xIndex = xCol.vectorizedData[i];
		let yIndex = isSame ? 0 : yCol.vectorizedData[i];
		counts[xIndex][yIndex] += 1;
	}

	if (state.plotRadialsSelected === PAIR_PLOT_RADIALS.percentage) {
		for (let i = 0; i < counts.length; i++) {
			let tots = 0;
			for (let j = 0; j < counts[i].length; j++) {
				tots += counts[i][j];
			}
			for (let j = 0; j < counts[i].length; j++) {
				counts[i][j] = (100 * counts[i][j]) / Math.max(tots, 1);
			}
		}
	} else if (state.plotRadialsSelected === PAIR_PLOT_RADIALS.total_percent) {
		for (let i = 0; i < counts[0].length; i++) {
			let tots = 0;
			for (let j = 0; j < counts.length; j++) {
				tots += counts[j][i];
			}
			for (let j = 0; j < counts.length; j++) {
				counts[j][i] = (100 * counts[j][i]) / Math.max(tots, 1);
			}
		}
	}

	let colors = getBarColors(Array(counts[0].length).fill(""));
	if (isSame) {
		colors = getBarColorsSame(Array(counts.length).fill(""));
	}

	const header: any[] = [""];
	for (let i = 0; i < nClassesY; i++) {
		header.push(yCol.getClassByIndex(i, true));
		header.push({ role: "style" });
		header.push({ role: "tooltip" });
		header.push({ role: "annotation" });
	}
	if (counts[0].length === header.length) {
		header.push(yCol.getClassByIndex(312321, true));
		header.push({ role: "style" });
		header.push({ role: "tooltip" });
		header.push({ role: "annotation" });
	}

	data = [header];
	for (let i = 0; i < counts.length; i++) {
		let subRow = counts[i].map((val, j) => [
			counts[i][j],
			`fill-color: ${colors.colors[isSame ? i : j]}; fill-opacity: ${
				colors.alphas[isSame ? i : j]
			}; stroke-color: #76A7FA; stroke-width: 0;`,
			yCol.columnName + ": " + yCol.getClassByIndex(j, true),
			state.plotRadialsSelected === PAIR_PLOT_RADIALS.counts
				? counts[i][j]
				: formatNumber(counts[i][j]) + "%",
		]);
		var merged = [].concat.apply([], subRow);
		let row = [xCol.getClassByIndex(i, true), ...merged];
		data.push(row);
	}

	let maxCounts = max(counts);
	let left = 50;
	if (maxCounts >= 80) {
		left = 50;
	}
	if (maxCounts >= 1000) {
		left = 70;
	}
	if (maxCounts >= 10000) {
		left = 90;
	}

	return (
		<Chart
			style={{
				height: "123%",
				width: "100%",
			}}
			chartType="ColumnChart"
			loader={<div>Loading Chart</div>}
			data={data}
			options={{
				// Material design options
				colors: BAR_COLORS,
				legend: { position: "none" },
				chartArea: {
					width: "90%",
					height: "100%",
					top: 70,
					bottom: 80,
					left: left,
				},

				vAxis: {
					minorGridlines: { color: "white" },
					gridlines: { color: "rgb(220,220,220)" },
					baselineColor: "rgb(220,220,220)",
					textStyle: { fontName: FONTS },
					title: `Counts of '${yCol.columnName}'`,
				},
				hAxis: {
					minorGridlines: { color: "white" },
					baselineColor: "rgb(220,220,220)",
					gridlines: { color: "rgb(220,220,220)" },
					textStyle: { fontName: FONTS },
					title: xCol.columnName,
				},

				annotations: {
					alwaysOutside: true,
					textStyle: {
						color: PRIMARY_COLOR,
						fontSize: 14,
						fontFamily: FONTS,
					},
				},
			}}
		/>
	);
}

function CorrelationHeader({ name, value }) {
	const classes = useStyles();

	return (
		<div
			className={classes.titleCorContainer}
			style={{ visibility: "visible" }}
		>
			<div
				style={{
					marginTop: "10px",
					marginLeft: "0px",
					fontFamily: FONTS,
					fontWeight: "bold",
				}}
			>
				{name}
			</div>
			<div
				style={{
					marginLeft: "0px",
					marginTop: "2px",
					fontSize: "20px",
					fontWeight: "bold",
					fontFamily: FONTS,
					color: SECONDARY_COLOR,
					marginBottom: "20px",
				}}
			>
				{value}
			</div>
		</div>
	);
}

type lineType = { slope: string; int: string; r2?: string };

function TrendlineEq({ slope, int }: lineType) {
	let pn = int.startsWith("-") ? "-" : "+";
	int = int.replace("-", "");

	return (
		<div
			style={{
				width: "100%",
				height: "20px",
				justifyContent: "center",
				alignItems: "center",
				alignContent: "center",
				position: "relative",
				textAlign: "center",
				display: "flex",
				flexDirection: "row",
				fontSize: "15px",
				fontWeight: "bold",
				marginTop: "60px",
			}}
		>
			<span style={{ position: "relative", color: SECONDARY_COLOR }}>
				Trendline Equation:{" "}
			</span>
			<span
				style={{
					position: "relative",
					marginLeft: "10px",
				}}
			>
				y = {slope}x {pn} {int}
			</span>
			<HelpIcon
				title={
					"The line-of-best-fit equation. For example, y = 2x + 3 means y increases by 2 when x increases by 1."
				}
				marginLeft={"10px"}
			></HelpIcon>
		</div>
	);
}

export function DualChart({ state }: stateType) {
	const classes = useStyles();

	const cols = getCol(state);
	let added = "";
	const isBar =
		cols.xCol.columnType === DATA_TYPES.categorical &&
		cols.yCol.columnType === DATA_TYPES.categorical;
	if (isBar) {
		added = ` - ${state.plotRadialsSelected}`;
	}

	// Find Correlation
	const cors = state.saveState.correlations.correlations;
	let ndx1 = 0;
	let ndx2 = 0;
	let correlation = "";
	if (cors !== undefined) {
		for (let i = 0; i < cors.colNames.length; i++) {
			if (cols.xCol.columnName === cors.colNames[i]) {
				ndx1 = i;
			}
			if (cols.yCol.columnName === cors.colNames[i]) {
				ndx2 = i;
			}
		}
		correlation =
			formatNumber(Math.round(100 * cors.correlations[ndx1][ndx2])) + "%";
	}

	function getLineEq(cor: number): lineType {
		let { leadCol, followCol } = subSample(
			[...cols.yCol.vectorizedData],
			[...cols.xCol.vectorizedData]
		);
		if (cols.xCol.columnType === DATA_TYPES.categorical) {
			for (let i = 0; i < followCol.length; i++) {
				followCol[i] = convertToNumber(
					cols.xCol.getClassByIndex(followCol[i])
				);
			}
		}
		let followCol2: number[] = [];
		let leadCol2: number[] = [];
		for (let i = 0; i < followCol.length; i++) {
			if (!isNaN2(leadCol[i]) && !isNaN2(followCol[i])) {
				followCol2.push(followCol[i]);
				leadCol2.push(leadCol[i]);
			}
		}
		followCol = followCol2;
		leadCol = leadCol2;

		let y = leadCol;
		let x = followCol;
		var lr = {};
		var n = y.length;
		var sum_x = 0;
		var sum_y = 0;
		var sum_xy = 0;
		var sum_xx = 0;
		var sum_yy = 0;

		for (var i = 0; i < y.length; i++) {
			sum_x += x[i];
			sum_y += y[i];
			sum_xy += x[i] * y[i];
			sum_xx += x[i] * x[i];
			sum_yy += y[i] * y[i];
		}

		lr["slope"] =
			(n * sum_xy - sum_x * sum_y) / (n * sum_xx - sum_x * sum_x);
		lr["intercept"] = (sum_y - lr["slope"] * sum_x) / n;
		return {
			slope: formatNumber(lr["slope"]),
			int: formatNumber(lr["intercept"]),
			r2: "",
		};
	}

	let lineEq = { slope: "0", int: "0" };
	const includeTrendLineEq =
		cols.xCol.availableColumnTypes.includes(DATA_TYPES.number) &&
		cols.yCol.availableColumnTypes.includes(DATA_TYPES.number);
	if (cors !== undefined && includeTrendLineEq) {
		lineEq = getLineEq(cors.correlations[ndx1][ndx2]);
	}

	let withHeader = !(
		cols.xCol.columnType === DATA_TYPES.categorical &&
		cols.yCol.columnType === DATA_TYPES.categorical
	);

	let scatterHeader = cols.xCol.availableColumnTypes.includes(
		DATA_TYPES.number
	);

	const includeLegend =
		cols.xCol.columnType === DATA_TYPES.categorical &&
		cols.yCol.columnType === DATA_TYPES.categorical;
	return (
		<div className={classes.chartContainer}>
			<div style={{ display: "flex", flexDirection: "row" }}>
				{withHeader && scatterHeader ? (
					<CorrelationHeader
						name={"Correlation"}
						value={correlation}
					></CorrelationHeader>
				) : (
					<div></div>
				)}
			</div>
			<div className={classes.chartContainer2}>
				<div
					style={{
						height: "100%",
						width: "100%",
						marginLeft: "0%",
					}}
				>
					<GetChart
						state={state}
						xCol={cols.xCol}
						yCol={cols.yCol}
					></GetChart>
				</div>
			</div>
			{includeLegend ? (
				<Legend
					yCol={cols.yCol}
					isSame={cols.yCol.columnName === cols.xCol.columnName}
					visible={cols.xCol.columnName !== cols.yCol.columnName}
				></Legend>
			) : (
				<div></div>
			)}
			{cols.yCol.columnType === DATA_TYPES.categorical &&
			cols.xCol.columnType === DATA_TYPES.categorical ? (
				<RadioButtonsGroup state={state}></RadioButtonsGroup>
			) : (
				<div></div>
			)}
			{includeTrendLineEq ? (
				<TrendlineEq
					slope={lineEq.slope}
					int={lineEq.int}
				></TrendlineEq>
			) : (
				<div></div>
			)}
		</div>
	);
}
